/**
 * UI gates for Stripe / online-payment CTAs.
 *
 * Canonical backend contracts (already enforced server-side):
 * - checkout: payment_methods.stripe_checkout_enabled (+ stripe.enabled + keys)
 *   exposed as StripeStatus.checkout_enabled, or enabled `stripe_checkout`
 *   entry from GET …/payment-methods
 * - Stripe master / Billing Portal / saved-card setup: stripe.enabled
 *   exposed as StripeStatus.enabled
 *
 * Fail closed: unknown / missing flags hide actionable Stripe CTAs.
 */

const ONLINE_STRIPE_METHOD_CODES = new Set(['stripe_checkout', 'stripe_invoice']);

export function paymentMethodsIncludeOnlineStripe(methods) {
  if (!Array.isArray(methods)) {
    return false;
  }
  return methods.some((entry) => {
    if (entry == null) {
      return false;
    }
    if (typeof entry === 'string') {
      return ONLINE_STRIPE_METHOD_CODES.has(entry);
    }
    const code = entry.code || entry.id || entry.method || entry.payment_method;
    return ONLINE_STRIPE_METHOD_CODES.has(code);
  });
}

export function checkoutEnabledFromStripeStatus(status) {
  return status?.checkout_enabled === true;
}

export function stripeEnabledFromStripeStatus(status) {
  return status?.enabled === true;
}

/**
 * @param {{ checkoutEnabled?: boolean, stripeEnabled?: boolean }} flags
 */
export function stripeOnlinePaymentControls({ checkoutEnabled = false, stripeEnabled = false } = {}) {
  const checkoutOn = checkoutEnabled === true;
  const stripeOn = stripeEnabled === true;

  return {
    showPayOnlineCta: checkoutOn,
    showCreateCheckoutCta: checkoutOn,
    showStripePortalCta: stripeOn,
    // Saved-card setup requires stripe.enabled AND checkout_enabled server-side.
    showSavedCardSection: stripeOn && checkoutOn,
  };
}

export function stripeOnlinePaymentControlsFromStripeStatus(status) {
  return stripeOnlinePaymentControls({
    checkoutEnabled: checkoutEnabledFromStripeStatus(status),
    stripeEnabled: stripeEnabledFromStripeStatus(status),
  });
}

/**
 * Portal surfaces without /system/stripe/status: payment-methods lists only
 * enabled methods. Checkout presence implies online Stripe checkout is on.
 * Billing Portal / saved-card need stripe.enabled; without a portal status
 * endpoint we keep portal+saved-card fail-closed unless checkout is enabled
 * (production keeps both flags aligned off).
 */
export function stripeOnlinePaymentControlsFromPaymentMethods(methods) {
  const online = paymentMethodsIncludeOnlineStripe(methods);
  return stripeOnlinePaymentControls({
    checkoutEnabled: online,
    stripeEnabled: online,
  });
}
